import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Typography,
    Box,
    Chip,
    LinearProgress,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Grid,
    Stack,
    Menu,
    MenuItem,
    Tooltip,
    ListItemIcon
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Edit as EditIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Settings as SettingsIcon,
    PersonAdd as PersonAddIcon,
    MoreVert as MoreVertIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import useTranslation from '../hooks/useTranslation';
import { es, enUS } from 'date-fns/locale';

const TourDatesManager = ({ tourId }) => {
    const { t } = useTranslation();
    const [dates, setDates] = useState([]);
    const [openAddDate, setOpenAddDate] = useState(false);
    const [openAddParticipant, setOpenAddParticipant] = useState(false);
    const [openEditParticipant, setOpenEditParticipant] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [newDate, setNewDate] = useState(null);
    const [totalStock, setTotalStock] = useState('');
    const [expandedDate, setExpandedDate] = useState(null);
    const [expandedParticipant, setExpandedParticipant] = useState(null);
    const [newParticipant, setNewParticipant] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [editParticipant, setEditParticipant] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [openEditDate, setOpenEditDate] = useState(false);
    const [editDate, setEditDate] = useState({
        date: null,
        totalStock: ''
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedDateForMenu, setSelectedDateForMenu] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    useEffect(() => {
        fetchDates();
    }, [tourId]);

    const fetchDates = async () => {
        try {
            const response = await axios.get(`/operators/tours/${tourId}/dates`);
            setDates(response.data);
        } catch (error) {
            console.error('Error fetching dates:', error);
        }
    };

    const handleAddDate = async () => {
        try {
            await axios.post(`/operators/tours/${tourId}/dates`, {
                date: newDate,
                totalStock: parseInt(totalStock)
            });
            setOpenAddDate(false);
            setNewDate(null);
            setTotalStock('');
            fetchDates();
            toast.success(t('pages.tours.operator.tourManagement.notifications.dateAdded'));
        } catch (error) {
            console.error('Error adding date:', error);
            toast.error(t('pages.tours.operator.tourManagement.notifications.dateAddError'));
        }
    };

    const handleAddParticipant = async () => {
        try {
            const payload = {
                firstName: newParticipant.firstName,
                lastName: newParticipant.lastName,
                email: newParticipant.email,
                phone: newParticipant.phone
            };
            
            await axios.post(
                `/operators/tours/dates/${selectedDate.id}/participants`,
                payload
            );
            setOpenAddParticipant(false);
            setNewParticipant({
                firstName: '',
                lastName: '',
                email: '',
                phone: ''
            });
            fetchDates();
            toast.success(t('pages.tours.operator.tourManagement.notifications.participantAdded'));
        } catch (error) {
            console.error('Error adding participant:', error);
            if (error.response?.data?.errors) {
                const errorMessage = error.response.data.errors.map(err => err.message).join(', ');
                toast.error(`${t('pages.tours.operator.tourManagement.notifications.participantAddError')}: ${errorMessage}`);
            } else {
                toast.error(t('pages.tours.operator.tourManagement.notifications.participantAddError'));
            }
        }
    };

    const handleEditParticipant = async () => {
        try {
            await axios.put(
                `/operators/tours/dates/${selectedDate.id}/participants/${selectedParticipant.id}`,
                editParticipant
            );
            setOpenEditParticipant(false);
            fetchDates();
            toast.success(t('pages.tours.operator.tourManagement.notifications.participantUpdated'));
        } catch (error) {
            console.error('Error updating participant:', error);
            if (error.response?.data?.errors) {
                const errorMessage = error.response.data.errors.map(err => err.message).join(', ');
                toast.error(`${t('pages.tours.operator.tourManagement.notifications.participantUpdateError')}: ${errorMessage}`);
            } else if (error.response?.status === 404) {
                toast.error(t('pages.tours.operator.tourManagement.notifications.participantNotFound'));
            } else {
                toast.error(t('pages.tours.operator.tourManagement.notifications.participantUpdateError'));
            }
        }
    };

    const handleRemoveParticipant = async (dateId, participantId) => {
        if (window.confirm('Are you sure you want to remove this participant?')) {
            try {
                await axios.delete(
                    `/operators/tours/dates/${dateId}/participants/${participantId}`
                );
                fetchDates();
            } catch (error) {
                console.error('Error removing participant:', error);
            }
        }
    };

    const getStockPercentage = (date) => {
        return ((date.total_stock - date.current_stock) / date.total_stock) * 100;
    };

    const getParticipantName = (participant) => {
        if (participant.TourGuest) {
            return `${participant.TourGuest.first_name || ''} ${participant.TourGuest.last_name || ''}`;
        }
        if (participant.User) {
            return `${participant.User.firstName || ''} ${participant.User.lastName || ''}`;
        }
        return 'N/A';
    };

    const getParticipantEmail = (participant) => {
        if (participant.TourGuest) {
            return participant.TourGuest.email;
        }
        if (participant.User) {
            return participant.User.email;
        }
        return 'N/A';
    };

    const getParticipantPhone = (participant) => {
        if (participant.TourGuest) {
            return participant.TourGuest.phone;
        }
        if (participant.User) {
            return participant.User.phone || 'N/A';
        }
        return 'N/A';
    };

    const handleOpenEditParticipant = (date, participant) => {
        setSelectedDate(date);
        setSelectedParticipant(participant);
        const participantData = participant.TourGuest || participant.User;
        setEditParticipant({
            firstName: (participant.TourGuest?.first_name || participant.User?.firstName || ''),
            lastName: (participant.TourGuest?.last_name || participant.User?.lastName || ''),
            email: (participant.TourGuest?.email || participant.User?.email || ''),
            phone: (participant.TourGuest?.phone || participant.User?.phone || '')
        });
        setOpenEditParticipant(true);
    };

    const handleEditDate = async () => {
        try {
            await axios.put(`/operators/tours/${tourId}/dates/${selectedDate.id}`, {
                date: editDate.date,
                totalStock: parseInt(editDate.totalStock)
            });
            setOpenEditDate(false);
            setEditDate({ date: null, totalStock: '' });
            fetchDates();
            toast.success(t('pages.tours.operator.tourManagement.notifications.dateUpdated'));
        } catch (error) {
            console.error('Error updating date:', error);
            toast.error(t('pages.tours.operator.tourManagement.notifications.dateUpdateError'));
        }
    };

    const handleOpenEditDate = (date) => {
        setSelectedDate(date);
        setEditDate({
            date: new Date(date.date),
            totalStock: date.total_stock.toString()
        });
        setOpenEditDate(true);
    };

    const handleMenuClick = (event, date) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedDateForMenu(date);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedDateForMenu(null);
    };

    const downloadParticipantsList = async () => {
        try {
            const participants = selectedDateForMenu.participants;

            const worksheetData = [
                ['Name', 'Email', 'Phone', 'Booking Date'], // Headers
                ...participants.map(p => [
                    getParticipantName(p),
                    getParticipantEmail(p),
                    getParticipantPhone(p),
                    format(new Date(p.booking_date), 'MMM d, yyyy h:mm a')
                ])
            ];

            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet(worksheetData);

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Participants');

            // Generate filename with tour date
            const dateStr = format(new Date(selectedDateForMenu.date), 'yyyy-MM-dd');
            const fileName = `tour_participants_${dateStr}.xlsx`;

            // Download file
            XLSX.writeFile(wb, fileName);
            
            handleMenuClose();
            toast.success('Participants list downloaded successfully');
        } catch (error) {
            console.error('Error downloading participants list:', error);
            toast.error('Failed to download participants list');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#316C6E' }}>
                    {t('pages.tours.operator.tourManagement.dates.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddDate(true)}
                    sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                >
                    {t('pages.tours.operator.tourManagement.dates.addDate')}
                </Button>
            </Box>

            {dates.map((date) => (
                <Paper key={date.id} sx={{ mb: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                    <Box sx={{ p: 2 }}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} md={3}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {format(new Date(date.date), 'MMM do, yyyy', { locale: t('common.locale') === 'es' ? es : enUS })}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="body2">
                                        {t('pages.tours.operator.tourManagement.dates.available')}: {date.current_stock} {t('pages.tours.operator.tourManagement.dates.of')} {date.total_stock}
                                    </Typography>
                                    <Chip
                                        label={`${date.participants?.length || 0} ${t('pages.tours.operator.tourManagement.dates.participants')}`}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={getStockPercentage(date)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#316C6E'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <IconButton
                                    onClick={(e) => handleMenuClick(e, date)}
                                    sx={{ mr: 1, color: '#316C6E' }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setExpandedDate(expandedDate === date.id ? null : date.id)}
                                >
                                    {expandedDate === date.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>

                    <Collapse in={expandedDate === date.id}>
                        <Divider />
                        <List sx={{ bgcolor: '#f8f9fa' }}>
                            {date.participants?.map((participant) => (
                                <React.Fragment key={participant.id}>
                                    <ListItem>
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ mr: 1, color: '#666' }} />
                                                        <Typography>
                                                            {getParticipantName(participant)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                                        {t('pages.tours.operator.tourManagement.dates.bookedAt')}: {format(new Date(participant.booking_date), 'MMM d, yyyy h:mm a', { locale: t('common.locale') === 'es' ? es : enUS })}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={1}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setExpandedParticipant(expandedParticipant === participant.id ? null : participant.id)}
                                                    >
                                                        {expandedParticipant === participant.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditParticipant(date, participant)}
                                                        sx={{ color: '#316C6E' }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveParticipant(date.id, participant.id)}
                                                        sx={{ color: '#d32f2f' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                            <Collapse in={expandedParticipant === participant.id}>
                                                <Grid container spacing={2} sx={{ mt: 1, pl: 4 }}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <EmailIcon sx={{ mr: 1, fontSize: 'small', color: '#666' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {getParticipantEmail(participant)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <PhoneIcon sx={{ mr: 1, fontSize: 'small', color: '#666' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {getParticipantPhone(participant)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Collapse>
                                        </Box>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                            {!date.participants?.length && (
                                <ListItem>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('pages.tours.operator.tourManagement.dates.noParticipantsYet')}
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </Collapse>
                </Paper>
            ))}

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { 
                        mt: 1,
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 2
                    }
                }}
            >
                <MenuItem onClick={() => {
                    setSelectedDate(selectedDateForMenu);
                    setOpenAddParticipant(true);
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <PersonAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('pages.tours.operator.tourManagement.dates.menuOptions.addParticipant')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={downloadParticipantsList}>
                    <ListItemIcon>
                        <PrintIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('pages.tours.operator.tourManagement.dates.menuOptions.downloadList')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    handleOpenEditDate(selectedDateForMenu);
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('pages.tours.operator.tourManagement.dates.menuOptions.editDate')}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Add Date Dialog */}
            <Dialog open={openAddDate} onClose={() => setOpenAddDate(false)}>
                <DialogTitle>{t('pages.tours.operator.tourManagement.dates.addDate')}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={t('common.locale') === 'es' ? es : enUS}>
                        <DateTimePicker
                            label={t('pages.tours.operator.tourManagement.dates.date')}
                            value={newDate}
                            onChange={setNewDate}
                            renderInput={(params) => <TextField {...params} fullWidth sx={{ mt: 2 }} />}
                        />
                    </LocalizationProvider>
                    <TextField
                        label={t('pages.tours.operator.tourManagement.dates.stock')}
                        type="number"
                        value={totalStock}
                        onChange={(e) => setTotalStock(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDate(false)}>
                        {t('pages.tours.operator.tourManagement.participants.cancel')}
                    </Button>
                    <Button 
                        onClick={handleAddDate} 
                        variant="contained"
                        sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                    >
                        {t('pages.tours.operator.tourManagement.dates.addDate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Participant Dialog */}
            <Dialog open={openAddParticipant} onClose={() => setOpenAddParticipant(false)}>
                <DialogTitle>{t('pages.tours.operator.tourManagement.participants.addParticipant')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label={t('pages.tours.operator.tourManagement.participants.firstName')}
                                value={newParticipant.firstName}
                                onChange={(e) => setNewParticipant({ ...newParticipant, firstName: e.target.value })}
                                fullWidth
                                sx={{ mt: 2 }}
                                error={!newParticipant.firstName}
                                helperText={!newParticipant.firstName ? "First name is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label={t('pages.tours.operator.tourManagement.participants.lastName')}
                                value={newParticipant.lastName}
                                onChange={(e) => setNewParticipant({ ...newParticipant, lastName: e.target.value })}
                                fullWidth
                                sx={{ mt: 2 }}
                                error={!newParticipant.lastName}
                                helperText={!newParticipant.lastName ? "Last name is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                label={t('pages.tours.operator.tourManagement.participants.email')}
                                type="email"
                                value={newParticipant.email}
                                onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                                fullWidth
                                error={!newParticipant.email}
                                helperText={!newParticipant.email ? "Email is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('pages.tours.operator.tourManagement.participants.phone')}
                                value={newParticipant.phone}
                                onChange={(e) => setNewParticipant({ ...newParticipant, phone: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddParticipant(false)}>
                        {t('pages.tours.operator.tourManagement.participants.cancel')}
                    </Button>
                    <Button 
                        onClick={handleAddParticipant} 
                        variant="contained"
                        sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                        disabled={!newParticipant.firstName || !newParticipant.lastName || !newParticipant.email}
                    >
                        {t('pages.tours.operator.tourManagement.participants.addParticipant')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Participant Dialog */}
            <Dialog open={openEditParticipant} onClose={() => setOpenEditParticipant(false)}>
                <DialogTitle>{t('pages.tours.operator.tourManagement.participants.editParticipant')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('pages.tours.operator.tourManagement.participants.firstName')}
                                value={editParticipant.firstName}
                                onChange={(e) => setEditParticipant({ ...editParticipant, firstName: e.target.value })}
                                fullWidth
                                sx={{ mt: 2 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('pages.tours.operator.tourManagement.participants.lastName')}
                                value={editParticipant.lastName}
                                onChange={(e) => setEditParticipant({ ...editParticipant, lastName: e.target.value })}
                                fullWidth
                                sx={{ mt: 2 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('pages.tours.operator.tourManagement.participants.email')}
                                value={editParticipant.email}
                                onChange={(e) => setEditParticipant({ ...editParticipant, email: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('pages.tours.operator.tourManagement.participants.phone')}
                                value={editParticipant.phone}
                                onChange={(e) => setEditParticipant({ ...editParticipant, phone: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditParticipant(false)}>
                        {t('pages.tours.operator.tourManagement.participants.cancel')}
                    </Button>
                    <Button 
                        onClick={handleEditParticipant} 
                        variant="contained"
                        sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                    >
                        {t('pages.tours.operator.tourManagement.participants.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Date Dialog */}
            <Dialog open={openEditDate} onClose={() => setOpenEditDate(false)}>
                <DialogTitle>{t('pages.tours.operator.tourManagement.dates.editDate')}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={t('common.locale') === 'es' ? es : enUS}>
                        <DateTimePicker
                            label={t('pages.tours.operator.tourManagement.dates.date')}
                            value={editDate.date}
                            onChange={(newDate) => setEditDate({ ...editDate, date: newDate })}
                            renderInput={(params) => <TextField {...params} fullWidth sx={{ mt: 2 }} />}
                        />
                    </LocalizationProvider>
                    <TextField
                        label={t('pages.tours.operator.tourManagement.dates.stock')}
                        type="number"
                        value={editDate.totalStock}
                        onChange={(e) => setEditDate({ ...editDate, totalStock: e.target.value })}
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDate(false)}>
                        {t('pages.tours.operator.tourManagement.participants.cancel')}
                    </Button>
                    <Button 
                        onClick={handleEditDate} 
                        variant="contained"
                        sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                    >
                        {t('pages.tours.operator.tourManagement.participants.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TourDatesManager; 